import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PencilSquareIcon, XMarkIcon, CheckCircleIcon, LockOpenIcon,
  XCircleIcon, ChartBarIcon, PlusIcon,
  ChevronRightIcon, PaperClipIcon,
} from '@heroicons/react/24/outline';
import { TermSettingsEditor } from '../components/TermSettings';

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}-${dt.toLocaleDateString('en-US',{month:'short'})}-${dt.getFullYear()}`;
};
const curYear   = () => { const y = new Date().getFullYear(); return `${y}-${y+1}`; };
const fmtMarks  = (n) => (n === null || n === undefined || n === '') ? '—' : Number(n) % 1 === 0 ? String(Number(n)) : Number(n).toFixed(1);
const SUBJ_COLORS = ['#3b6ef8','#10c98e','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#84cc16','#f97316'];

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .aa2 * { box-sizing:border-box; }
  .aa2 { font-family:'Sora',sans-serif; color:#1a1d2e; animation:aa2Fade .4s ease both; }
  @keyframes aa2Fade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .aa2-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .aa2-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .aa2-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue   { background:#3b6ef8; color:white; } .btn-blue:hover   { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); }
  .btn-green  { background:#10c98e; color:white; } .btn-green:hover  { background:#0db87e; }
  .btn-ghost  { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover  { background:#e4e7f0; }
  .btn-red    { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-amber  { background:#fffbeb; color:#f59e0b; border:1px solid #fde68a; } .btn-amber:hover { background:#f59e0b; color:white; }
  .btn-purple { background:#8b5cf6; color:white; } .btn-purple:hover { background:#7c3aed; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .breadcrumb { display:flex; align-items:center; gap:6px; margin-bottom:1.2rem; flex-wrap:wrap; }
  .bc-item { font-size:.78rem; font-weight:600; color:#9094a8; cursor:pointer; transition:color .15s; }
  .bc-item:hover { color:#3b6ef8; } .bc-item.active { color:#0f1123; cursor:default; }
  .bc-sep { color:#d1d5db; font-size:.82rem; }
  .card-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:1rem; margin-bottom:1rem; }
  .grade-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.1rem 1.2rem; cursor:pointer; transition:all .15s; }
  .grade-card:hover { border-color:#3b6ef8; box-shadow:0 4px 16px rgba(59,110,248,.1); transform:translateY(-1px); }
  .subj-card  { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.2rem; cursor:pointer; transition:all .15s; }
  .subj-card:hover  { border-color:#8b5cf6; box-shadow:0 4px 16px rgba(139,92,246,.12); transform:translateY(-1px); }
  .gc-name { font-size:.95rem; font-weight:700; color:#0f1123; margin-bottom:3px; }
  .gc-sub  { font-size:.72rem; color:#9094a8; }
  .gc-arrow { display:flex; align-items:center; justify-content:space-between; margin-top:10px; }
  .gc-arrow svg { width:15px; height:15px; stroke:#3b6ef8; stroke-width:2; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .row-item { display:flex; align-items:center; justify-content:space-between; padding:13px 18px; border-bottom:1px solid #f5f6fb; gap:10px; flex-wrap:wrap; cursor:pointer; transition:background .12s; }
  .row-item:last-child { border-bottom:none; }
  .row-item:hover { background:#fafbff; }
  .row-title   { font-size:.83rem; font-weight:600; color:#1a1d2e; }
  .row-sub     { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .row-desc    { font-size:.72rem; color:#6b7194; margin-top:3px; font-style:italic; }
  .row-actions { display:flex; gap:6px; align-items:center; flex-shrink:0; flex-wrap:wrap; }
  .status-badge { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; white-space:nowrap; }
  .status-badge.draft            { background:#f3f4f6; color:#6b7280; }
  .status-badge.submitted        { background:#ecfdf5; color:#10c98e; }
  .status-badge.locked           { background:#eef2ff; color:#3b6ef8; }
  .status-badge.unlock_requested { background:#fffbeb; color:#f59e0b; }
  .status-badge.unlocked         { background:#fdf4ff; color:#a855f7; }
  .clo-chip { display:inline-flex; align-items:center; gap:3px; font-size:.65rem; font-weight:700; padding:2px 7px; border-radius:6px; background:#eef2ff; color:#3b6ef8; font-family:'JetBrains Mono',monospace; white-space:nowrap; }
  .marks-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .marks-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
  .marks-table { width:100%; border-collapse:collapse; }
  .marks-table thead { background:#fafbff; border-bottom:2px solid #edf0f9; }
  .marks-table th { padding:10px 12px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .marks-table tbody tr { border-bottom:1px solid #f3f5fb; }
  .marks-table tbody tr:last-child { border-bottom:none; }
  .marks-table td { padding:8px 12px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .marks-input { width:80px; font-family:'JetBrains Mono',monospace; font-size:.82rem; padding:6px 8px; border:1.5px solid #e0e4f0; border-radius:7px; background:#f8f9fc; text-align:center; outline:none; }
  .marks-input:focus { border-color:#3b6ef8; background:white; }
  .marks-input:disabled { background:#f3f4f6; color:#b0b5cc; }
  .chk { width:16px; height:16px; cursor:pointer; }
  .student-name { font-weight:600; font-size:.8rem; color:#0f1123; }
  .student-roll { font-size:.7rem; color:#9094a8; margin-top:1px; font-family:'JetBrains Mono',monospace; }
  .marks-cards { display:none; padding:10px; }
  .marks-card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .mc-name { font-weight:700; font-size:.84rem; color:#0f1123; margin-bottom:2px; }
  .mc-roll { font-size:.7rem; color:#9094a8; font-family:'JetBrains Mono',monospace; margin-bottom:8px; }
  .mc-row  { display:flex; align-items:center; justify-content:space-between; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .mc-row:last-child { border-bottom:none; }
  .mc-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .mc-checks { display:flex; gap:16px; }
  .mc-check-item { display:flex; align-items:center; gap:5px; font-size:.76rem; color:#6b7194; }
  .admin-banner { background:#eef2ff; border:1px solid #c7d2fe; border-radius:12px; padding:10px 16px; margin-bottom:14px; font-size:.78rem; color:#3730a3; }
  .aa2-empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .aa2-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  /* Unlock review modal */
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:16px; flex-wrap:wrap; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; display:block; margin-bottom:5px; }
  .form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; resize:vertical; min-height:70px; }
  .form-textarea:focus { border-color:#3b6ef8; background:white; }
  .unlock-reason { background:#fffbeb; border:1px solid #fde68a; border-radius:10px; padding:10px 14px; margin-bottom:14px; font-size:.78rem; color:#92400e; }
  .unlock-reason strong { display:block; font-weight:700; margin-bottom:3px; }

  @media (max-width:768px) {
    .aa2-title { font-size:1.1rem; }
    .card-grid { grid-template-columns:1fr 1fr; }
    .marks-table-wrap { display:none !important; }
    .marks-cards { display:block; }
    .marks-header { flex-direction:column; gap:10px; }
    .marks-actions .btn { flex:1; justify-content:center; }
  }
`;

export default function AdminAssessments() {
  // Navigation
  const [navStep,        setNavStep]       = useState('grades');
  const [activeClass,    setActiveClass]   = useState(null);
  const [activeSection,  setActiveSection] = useState('');
  const [activeSubject,  setActiveSubject] = useState('');

  // Data
  const [classes,        setClasses]       = useState([]);
  const [subjects,       setSubjects]      = useState([]); // derived from assessments
  const [assessments,    setAssessments]   = useState([]);
  const [selected,       setSelected]      = useState(null);
  const [marksData,      setMarksData]     = useState([]);
  const [loading,        setLoading]       = useState(false);
  const [saving,         setSaving]        = useState(false);
  const [obeEnabled,     setObeEnabled]    = useState(false);

  // Unlock review modal
  const [reviewModal,    setReviewModal]   = useState(null);
  const [adminNote,      setAdminNote]     = useState('');
  const [processing,     setProcessing]    = useState(false);
  const [isTermMode,     setIsTermMode]    = useState(false);
  const [termPeriods,    setTermPeriods]   = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/admin/settings/assessment-mode').then(r => {
        if (r.data?.assessmentMode === 'term') {
          setIsTermMode(true);
          return api.get('/admin/term-periods').then(t => setTermPeriods(t.data || []));
        }
      }).catch(()=>{}),
      api.get('/admin/classes').then(r => setClasses(r.data?.classes || r.data || [])).catch(()=>{}),
      api.get('/admin/clos/settings').then(r => setObeEnabled(!!r.data?.obeEnabled)).catch(()=>{}),
    ]).finally(() => setLoading(false));
  }, []);

  // ── Fetch assessments for class+section+subject ────────────────────────────
  const fetchAssessments = useCallback(async (classId, section, subject) => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ classId, section, subject });
      const { data } = await api.get(`/admin/assessments?${p}`);
      setAssessments(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load assessments'); }
    finally { setLoading(false); }
  }, []);

  // ── Fetch subjects available for a class+section (from assessments) ─────────
  const fetchSubjects = useCallback(async (classId, section) => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ classId, section });
      const { data } = await api.get(`/admin/assessments?${p}`);
      const unique = [...new Set((Array.isArray(data) ? data : []).map(a => a.subject).filter(Boolean))].sort();
      setSubjects(unique);
    } catch { toast.error('Failed to load subjects'); }
    finally { setLoading(false); }
  }, []);

  // ── Navigation helpers ─────────────────────────────────────────────────────
  const goToGrades   = () => { setNavStep('grades'); setActiveClass(null); setActiveSection(''); setActiveSubject(''); };
  const goToSections = (cls) => { setActiveClass(cls); setActiveSection(''); setNavStep('sections'); };
  const goToSubjects = (sec) => {
    setActiveSection(sec);
    setNavStep('subjects');
    fetchSubjects(activeClass.id || activeClass._id, sec);
  };
  const goToList = (subject) => {
    setActiveSubject(subject);
    setNavStep('list');
    fetchAssessments(activeClass.id || activeClass._id, activeSection, subject);
  };

  // ── Open marks for an assessment ───────────────────────────────────────────
  const openMarks = async (assessment) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/${assessment._id || assessment.id}/marks`);
      setSelected(data);
      setMarksData((data.marks || []).map(m => ({
        studentId:    m.student?._id || m.student_id,
        name:         m.student?.user?.name || '—',
        rollNo:       m.student?.rollNo || '',
        admissionNo:  m.student?.admissionNo || '',
        marksObtained: m.marksObtained ?? m.marks_obtained ?? '',
        isAbsent:     m.isAbsent || m.is_absent || false,
        remarks:      m.remarks  || '',
      })));
      setNavStep('marks');
    } catch { toast.error('Failed to load marks'); }
    finally { setLoading(false); }
  };

  const updateMark = (idx, field, value) => {
    setMarksData(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const u = { ...r, [field]: value };
      if (field === 'isAbsent' && value) { u.marksObtained = ''; }
      return u;
    }));
  };

  const handleSaveMarks = async () => {
    setSaving(true);
    try {
      const payload = marksData.map(r => ({
        studentId:    r.studentId,
        marksObtained: r.isAbsent ? null : (r.marksObtained === '' ? null : Number(r.marksObtained)),
        isAbsent:     r.isAbsent,
        remarks:      r.remarks,
      }));
      await api.put(`/admin/assessments/${selected._id || selected.id}/marks`, { marks: payload });
      toast.success('Marks saved');
      setNavStep('list');
      fetchAssessments(activeClass.id || activeClass._id, activeSection, activeSubject);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save marks'); }
    finally { setSaving(false); }
  };

  // ── Unlock review ──────────────────────────────────────────────────────────
  const handleReview = async (action) => {
    setProcessing(true);
    try {
      await api.put(`/admin/assessments/${reviewModal._id || reviewModal.id}/review-unlock`, { action, adminNote });
      toast.success(action === 'approve' ? 'Unlocked successfully' : 'Request denied');
      setReviewModal(null); setAdminNote('');
      fetchAssessments(activeClass.id || activeClass._id, activeSection, activeSubject);
    } catch { toast.error('Failed'); }
    finally { setProcessing(false); }
  };

  // ── Breadcrumb ─────────────────────────────────────────────────────────────
  const Breadcrumb = () => (
    <div className="breadcrumb">
      <span className="bc-item" onClick={goToGrades}>Assessments</span>
      {activeClass && <>
        <span className="bc-sep">›</span>
        <span className={`bc-item ${navStep==='sections'?'active':''}`}
          onClick={() => { setNavStep('sections'); setActiveSection(''); setActiveSubject(''); }}>
          {activeClass.name}
        </span>
      </>}
      {activeSection && <>
        <span className="bc-sep">›</span>
        <span className={`bc-item ${navStep==='subjects'&&!activeSubject?'active':''}`}
          onClick={() => { setNavStep('subjects'); setActiveSubject(''); fetchSubjects(activeClass.id||activeClass._id, activeSection); }}>
          Section {activeSection}
        </span>
      </>}
      {activeSubject && <>
        <span className="bc-sep">›</span>
        <span className={`bc-item ${navStep==='list'?'active':''}`} onClick={() => setNavStep('list')}>
          {activeSubject}
        </span>
      </>}
      {navStep === 'marks' && <><span className="bc-sep">›</span><span className="bc-item active">Marks</span></>}
    </div>
  );

  // ── MARKS VIEW ─────────────────────────────────────────────────────────────
  if (navStep === 'marks' && selected) return (
    <div className="aa2"><style>{style}</style>
      <Breadcrumb />
      <div className="marks-header">
        <div>
          <div className="aa2-title">{selected.assessmentType?.name} — {selected.subject}</div>
          <div className="aa2-sub">
            {selected.class?.name} · Sec {selected.section}
            · Max: {fmtMarks(selected.max_marks ?? selected.maxMarks)}
            · {fmtDate(selected.date)}
            · By: <strong>{selected.createdBy?.name}</strong>
          </div>
          {selected.description && (
            <div style={{fontSize:'.76rem',color:'#6b7194',marginTop:4,fontStyle:'italic'}}>📝 {selected.description}</div>
          )}
          {isTermMode && (
            <TermSettingsEditor assessment={selected} terms={termPeriods} role="admin"
              onSaved={s => setSelected(prev => ({ ...prev, ...s }))} />
          )}
          {obeEnabled && selected.cloMappings?.length > 0 && (
            <div style={{display:'flex',alignItems:'center',gap:5,marginTop:6,flexWrap:'wrap'}}>
              <span style={{fontSize:'.68rem',color:'#9094a8',fontWeight:600}}>CLOs:</span>
              {selected.cloMappings.map((m,i) => (
                <span key={i} className="clo-chip">{m.clo?.code||'CLO'}{m.weightage&&m.weightage!==100?` ${m.weightage}%`:''}</span>
              ))}
            </div>
          )}
        </div>
        <div className="marks-actions">
          <span className={`status-badge ${selected.status}`}>{selected.status?.replace('_',' ')}</span>
          {selected.status === 'unlock_requested' && (
            <button className="btn btn-green btn-sm" onClick={() => { setReviewModal(selected); setAdminNote(''); }}>
              <LockOpenIcon />Review Unlock
            </button>
          )}
          <button className="btn btn-blue" onClick={handleSaveMarks} disabled={saving}>
            <CheckCircleIcon />{saving ? 'Saving…' : 'Save Marks'}
          </button>
        </div>
      </div>

      <div className="admin-banner">
        ✏️ <strong>Admin override:</strong> You can edit marks on any assessment regardless of its status.
      </div>

      {marksData.length === 0 ? (
        <div className="aa2-empty">No students found for this assessment.</div>
      ) : (
        <div className="panel">
          {/* Desktop table */}
          <div className="marks-table-wrap" style={{overflowX:'auto'}}>
            <table className="marks-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student</th>
                  <th>Marks / {fmtMarks(selected.max_marks ?? selected.maxMarks)}</th>
                  <th>Absent</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {marksData.map((m, i) => (
                  <tr key={m.studentId}>
                    <td style={{color:'#9094a8',fontFamily:"'JetBrains Mono',monospace",fontSize:'.74rem'}}>{i+1}</td>
                    <td>
                      <div className="student-name">{m.name}</div>
                      <div className="student-roll">Roll: {m.rollNo||'—'} · {m.admissionNo||''}</div>
                    </td>
                    <td>
                      <input type="number" className="marks-input"
                        value={m.isAbsent ? '' : (m.marksObtained ?? '')}
                        min={0} max={Number(selected.max_marks || selected.maxMarks) || 100}
                        disabled={m.isAbsent}
                        onChange={e => updateMark(i, 'marksObtained', e.target.value)} />
                    </td>
                    <td><input type="checkbox" className="chk" checked={m.isAbsent} onChange={e => updateMark(i,'isAbsent',e.target.checked)} /></td>
                    <td>
                      <input type="text" className="marks-input" style={{width:130}}
                        value={m.remarks} placeholder="remarks…"
                        onChange={e => updateMark(i,'remarks',e.target.value)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="marks-cards">
            {marksData.map((m, i) => (
              <div key={m.studentId} className="marks-card">
                <div className="mc-name">{m.name}</div>
                <div className="mc-roll">Roll: {m.rollNo||'—'} · {m.admissionNo||''}</div>
                <div className="mc-row">
                  <span className="mc-label">Marks / {fmtMarks(selected.max_marks??selected.maxMarks)}</span>
                  <input type="number" className="marks-input"
                    value={m.isAbsent?'':(m.marksObtained??'')}
                    min={0} max={Number(selected.max_marks||selected.maxMarks)||100}
                    disabled={m.isAbsent}
                    onChange={e => updateMark(i,'marksObtained',e.target.value)} />
                </div>
                <div className="mc-row">
                  <span className="mc-label">Status</span>
                  <div className="mc-checks">
                    <label className="mc-check-item"><input type="checkbox" checked={m.isAbsent} onChange={e=>updateMark(i,'isAbsent',e.target.checked)} /> Absent</label>
                  </div>
                </div>
                <div className="mc-row">
                  <span className="mc-label">Remarks</span>
                  <input type="text" className="marks-input" style={{width:130}}
                    value={m.remarks} placeholder="remarks…"
                    onChange={e => updateMark(i,'remarks',e.target.value)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ── ASSESSMENT LIST ────────────────────────────────────────────────────────
  if (navStep === 'list') return (
    <div className="aa2"><style>{style}</style>
      <Breadcrumb />
      <div className="aa2-header">
        <div>
          <div className="aa2-title">{activeSubject}</div>
          <div className="aa2-sub">{activeClass?.name} · Section {activeSection}</div>
        </div>
      </div>
      <div className="panel">
        <div className="panel-head">
          <div className="panel-title">Assessments ({assessments.length})</div>
        </div>
        {loading ? (
          <div className="aa2-loading"><div className="spinner"/>Loading…</div>
        ) : assessments.length === 0 ? (
          <div className="aa2-empty">No assessments found for {activeSubject} in this section.</div>
        ) : assessments.map(a => (
          <div key={a._id||a.id} className="row-item" onClick={() => openMarks(a)}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                <div className="row-title">{a.assessmentType?.name}</div>
                <span className={`status-badge ${a.status}`}>{a.status?.replace('_',' ')}</span>
                {obeEnabled && a.cloMappings?.length > 0 && <>
                  {a.cloMappings.slice(0,3).map((m,i) => <span key={i} className="clo-chip">{m.clo?.code||'CLO'}</span>)}
                  {a.cloMappings.length > 3 && <span className="clo-chip">+{a.cloMappings.length-3}</span>}
                </>}
              </div>
              <div className="row-sub">
                {fmtDate(a.date)} · Max: {fmtMarks(a.max_marks??a.maxMarks)} · By: {a.createdBy?.name}
              </div>
              {a.description && <div className="row-desc">"{a.description}"</div>}
              {a.status==='unlock_requested' && (
                <div style={{fontSize:'.7rem',color:'#f59e0b',marginTop:3}}>⏳ Awaiting unlock approval</div>
              )}
            </div>
            <div className="row-actions" onClick={e => e.stopPropagation()}>
              <button className="btn btn-blue btn-sm" onClick={() => openMarks(a)}>
                <PencilSquareIcon />Marks
              </button>
              {a.status==='unlock_requested' && (
                <button className="btn btn-green btn-sm" onClick={() => { setReviewModal(a); setAdminNote(''); }}>
                  <LockOpenIcon />Review
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Unlock review modal */}
      {reviewModal && createPortal(
        <div className="modal-backdrop" onClick={() => setReviewModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Review Unlock Request</div>
              <button className="modal-close" onClick={() => setReviewModal(null)}><XMarkIcon /></button>
            </div>
            {reviewModal.unlockRequest?.reason && (
              <div className="unlock-reason"><strong>Teacher's reason:</strong>{reviewModal.unlockRequest.reason}</div>
            )}
            <div style={{fontSize:'.78rem',color:'#6b7194',marginBottom:14}}>
              <strong style={{color:'#0f1123'}}>{reviewModal.assessmentType?.name}</strong>
              {' — '}{reviewModal.subject}{' — '}{reviewModal.class?.name} / {reviewModal.section}
              <br/>By: {reviewModal.createdBy?.name}
            </div>
            <div style={{marginBottom:12}}>
              <label className="form-label">Note to teacher (optional)</label>
              <textarea className="form-textarea" value={adminNote} onChange={e=>setAdminNote(e.target.value)} placeholder="Add a note…"/>
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setReviewModal(null)}>Cancel</button>
              <button className="btn btn-red" onClick={() => handleReview('deny')} disabled={processing}><XCircleIcon />Deny</button>
              <button className="btn btn-green" onClick={() => handleReview('approve')} disabled={processing}><LockOpenIcon />{processing?'Processing…':'Approve'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );

  // ── SUBJECTS VIEW ──────────────────────────────────────────────────────────
  if (navStep === 'subjects' && activeClass) return (
    <div className="aa2"><style>{style}</style>
      <Breadcrumb />
      <div className="aa2-header">
        <div>
          <div className="aa2-title">Section {activeSection}</div>
          <div className="aa2-sub">{activeClass.name} · Select a subject</div>
        </div>
      </div>
      {loading ? (
        <div className="aa2-loading"><div className="spinner"/>Loading…</div>
      ) : subjects.length === 0 ? (
        <div className="aa2-empty">No assessments found for this section yet.</div>
      ) : (
        <div className="card-grid">
          {subjects.map((subj, i) => (
            <div key={subj} className="subj-card" onClick={() => goToList(subj)}>
              <div style={{width:32,height:32,borderRadius:9,background:`${SUBJ_COLORS[i%SUBJ_COLORS.length]}18`,color:SUBJ_COLORS[i%SUBJ_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.72rem',marginBottom:8}}>
                {subj.slice(0,2).toUpperCase()}
              </div>
              <div className="gc-name">{subj}</div>
              <div className="gc-arrow">
                <span style={{fontSize:'.72rem',color:SUBJ_COLORS[i%SUBJ_COLORS.length],fontWeight:600}}>View assessments</span>
                <ChevronRightIcon style={{width:15,height:15,stroke:SUBJ_COLORS[i%SUBJ_COLORS.length]}} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── SECTIONS VIEW ──────────────────────────────────────────────────────────
  if (navStep === 'sections' && activeClass) {
    const secs = (activeClass.sections || []).map(s => typeof s === 'string' ? s : s.name || String(s));
    return (
      <div className="aa2"><style>{style}</style>
        <Breadcrumb />
        <div className="aa2-header">
          <div><div className="aa2-title">{activeClass.name}</div><div className="aa2-sub">Select a section</div></div>
        </div>
        {secs.length === 0 ? <div className="aa2-empty">No sections found.</div> : (
          <div className="card-grid">
            {secs.map((sec, i) => (
              <div key={sec} className="grade-card" onClick={() => goToSubjects(sec)}>
                <div style={{width:36,height:36,borderRadius:10,background:`${SUBJ_COLORS[i%SUBJ_COLORS.length]}18`,color:SUBJ_COLORS[i%SUBJ_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.9rem',marginBottom:8}}>
                  {sec}
                </div>
                <div className="gc-name">Section {sec}</div>
                <div className="gc-arrow">
                  <span style={{fontSize:'.72rem',color:SUBJ_COLORS[i%SUBJ_COLORS.length],fontWeight:600}}>View subjects</span>
                  <ChevronRightIcon style={{width:15,height:15,stroke:SUBJ_COLORS[i%SUBJ_COLORS.length]}} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── GRADE CARDS (root) ─────────────────────────────────────────────────────
  return (
    <div className="aa2"><style>{style}</style>
      <div className="aa2-header">
        <div>
          <div className="aa2-title">Assessments</div>
          <div className="aa2-sub">Select a class to manage student marks</div>
        </div>
      </div>
      {loading ? (
        <div className="aa2-loading"><div className="spinner"/>Loading…</div>
      ) : classes.length === 0 ? (
        <div className="aa2-empty">No classes found.</div>
      ) : (
        <div className="card-grid">
          {classes.map((cls, i) => {
            const id = cls._id || cls.id;
            const secs = (cls.sections || []).map(s => typeof s === 'string' ? s : s.name || String(s));
            return (
              <div key={id} className="grade-card" onClick={() => goToSections(cls)}>
                <div style={{width:36,height:36,borderRadius:10,background:`${SUBJ_COLORS[i%SUBJ_COLORS.length]}18`,color:SUBJ_COLORS[i%SUBJ_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.8rem',marginBottom:8}}>
                  {cls.name?.replace('Grade ','G').slice(0,3)}
                </div>
                <div className="gc-name">{cls.name}</div>
                <div className="gc-sub">{secs.length} section{secs.length!==1?'s':''}</div>
                <div className="gc-arrow">
                  <span style={{fontSize:'.72rem',color:'#3b6ef8',fontWeight:600}}>Manage marks</span>
                  <ChevronRightIcon style={{width:15,height:15,stroke:'#3b6ef8'}} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}